import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Landmark, Users } from "lucide-react";
import { hero, heroPathways } from "../mock";

const iconMap = { Building2, Landmark, Users };

const HeroPremium = () => {
  return (
    <section className="relative overflow-hidden bg-white">
      {/* Right-side business district + bridge image, fading into white on the left */}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-full lg:w-[64%]">
        <img
          src={hero.image}
          alt="Business district connected by a bridge"
          className="h-full w-full object-cover object-[18%_center]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/10 lg:via-white/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-white/30" />
      </div>

      <div className="relative mx-auto flex min-h-[90vh] max-w-7xl items-center px-4 pb-16 pt-28 md:px-8 md:pt-32">
        <div className="max-w-2xl">
          <span className="fade-up mb-5 block text-xs font-bold uppercase tracking-[0.3em] text-[#B8862F]">
            {hero.kicker}
          </span>
          <h1 className="fade-up text-4xl font-extrabold uppercase leading-[1.05] tracking-tight text-[#16233B] sm:text-5xl md:text-[68px]">
            20 Years.
            <br />
            Across Borders.
          </h1>

          <div className="fade-up mt-7 h-1.5 w-28 rounded-full bg-[#B8862F]" />

          {/* Triad: Elevate · Engage · Expand */}
          <div className="fade-up mt-6 flex flex-wrap items-center gap-x-3 gap-y-1">
            {hero.triad.map((word, i) => (
              <React.Fragment key={word}>
                {i > 0 && (
                  <span className="text-xl font-bold text-[#B8862F]">·</span>
                )}
                <span
                  className={`text-lg font-bold uppercase tracking-[0.22em] md:text-xl ${
                    i === hero.triad.length - 1 ? "text-[#E4545F]" : "text-[#16233B]"
                  }`}
                >
                  {word}
                </span>
              </React.Fragment>
            ))}
          </div>
          <p className="fade-up mt-3 max-w-xl text-[15px] leading-relaxed text-slate-500">
            {hero.subline}
          </p>

          {/* Three colour-coded stakeholder pathways */}
          <div className="fade-up mt-12 grid max-w-3xl grid-cols-1 divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200/70 bg-white/85 shadow-xl backdrop-blur sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            {heroPathways.map((p) => {
              const Icon = iconMap[p.icon] || Building2;
              return (
                <Link
                  to={p.to}
                  key={p.id}
                  className="group flex items-start gap-3 p-5 transition-colors duration-200 hover:bg-slate-50"
                >
                  <span
                    className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-transform duration-300 group-hover:scale-105"
                    style={{ backgroundColor: p.color }}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-bold uppercase leading-snug text-[#16233B]">
                      {p.label}
                    </span>
                    <span className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-snug text-slate-500">
                      {p.line}
                      <ArrowRight
                        className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                        style={{ color: p.color }}
                      />
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroPremium;
